/**
 * WebSocket lifecycle management, reconnection, and message routing.
 */

import {
  MSG_SNAPSHOT,
  MSG_WORLD_SETUP,
  MSG_PLAYER_JOIN,
  MSG_PLAYER_LEAVE,
  MSG_ASTEROID_SYNC,
  MSG_SCENERY_SETUP,
  MSG_PARTICLE_DEFAULTS_UPDATED,
  MSG_REQUEST_PARTICLE_BROADCAST,
  getMessageType,
  decodeSnapshot,
  decodeWorldSetup,
  decodePlayerJoin,
  decodePlayerLeave,
  decodeAsteroidSync,
  decodeScenerySetup,
} from '@warmancer/shared';
import { useGameStore } from '../../stores/gameStore';
import { initClientPhysics, syncAsteroidPositions, setServerMoonAngle, removeRemoteShip } from './ClientPrediction';

const RECONNECT_DELAY_MS = 2000;
const MAX_RECONNECT_ATTEMPTS = 5;

export class NetworkManager {
  private ws: WebSocket | null = null;
  private url: string = '';
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private intentionalClose = false;
  private onParticleDefaultsUpdated: (() => void) | null = null;

  /**
   * Connect to the game server.
   * @param apiBaseUrl - Base URL of the API (e.g., "wss://api.warmancer.com" or "ws://localhost:8788")
   * @param sessionToken - User's session token
   */
  connect(apiBaseUrl: string, sessionToken: string): void {
    this.intentionalClose = false;
    this.url = `${apiBaseUrl}/game/connect?token=${encodeURIComponent(sessionToken)}`;
    useGameStore.getState().setConnectionStatus('connecting');
    this.createWebSocket();
  }

  disconnect(): void {
    this.intentionalClose = true;
    this.clearReconnect();
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    useGameStore.getState().setConnectionStatus('disconnected');
  }

  getWebSocket(): WebSocket | null {
    return this.ws;
  }

  /**
   * Set a callback to be invoked when the server broadcasts a particle defaults update.
   * The client should re-fetch defaults from the REST endpoint.
   */
  setOnParticleDefaultsUpdated(callback: (() => void) | null): void {
    this.onParticleDefaultsUpdated = callback;
  }

  /**
   * Request the server to broadcast a particle defaults update to all clients.
   * Called after saving new defaults via the REST API.
   */
  requestParticleDefaultsBroadcast(): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      const buf = new ArrayBuffer(1);
      new DataView(buf).setUint8(0, MSG_REQUEST_PARTICLE_BROADCAST);
      this.ws.send(buf);
    }
  }

  /**
   * Force a reconnection by closing the current WebSocket.
   * The onclose handler will trigger attemptReconnect() automatically.
   * Used by the snapshot watchdog when the DO hibernates and stops sending data.
   */
  forceReconnect(): void {
    if (this.ws) {
      this.ws.close();
    }
  }

  private createWebSocket(): void {
    if (this.ws) {
      this.ws.close();
    }

    const ws = new WebSocket(this.url);
    ws.binaryType = 'arraybuffer';

    ws.onopen = () => {
      this.reconnectAttempts = 0;
      useGameStore.getState().setConnectionStatus('connected');
    };

    ws.onmessage = (event) => {
      if (!(event.data instanceof ArrayBuffer)) return;
      this.handleMessage(event.data);
    };

    ws.onclose = () => {
      if (!this.intentionalClose) {
        this.attemptReconnect();
      }
    };

    ws.onerror = () => {
      // onclose will also fire, reconnection handled there
    };

    this.ws = ws;
  }

  private handleMessage(buf: ArrayBuffer): void {
    if (buf.byteLength === 0) return;

    const msgType = getMessageType(buf);
    const store = useGameStore.getState();

    switch (msgType) {
      case MSG_SNAPSHOT: {
        const snapshot = decodeSnapshot(buf);
        setServerMoonAngle(snapshot.moonAngle, snapshot.serverTick);
        store.updateFromSnapshot(
          snapshot.serverTick,
          snapshot.lastAckedInput,
          snapshot.entities,
          snapshot.moonAngle,
        );
        store.clearAckedInputs(snapshot.lastAckedInput);
        break;
      }
      case MSG_WORLD_SETUP: {
        const setup = decodeWorldSetup(buf);
        store.setLocalEntityId(setup.localEntityId);
        store.setWorldSetup(setup);
        // Initialize client physics with world data
        initClientPhysics(setup, setup.localEntityId);
        break;
      }
      case MSG_PLAYER_JOIN: {
        const join = decodePlayerJoin(buf);
        store.addRemotePlayer(join.entityId, join.username);
        break;
      }
      case MSG_PLAYER_LEAVE: {
        const leave = decodePlayerLeave(buf);
        store.removeRemotePlayer(leave.entityId);
        removeRemoteShip(leave.entityId);
        break;
      }
      case MSG_ASTEROID_SYNC: {
        const asteroids = decodeAsteroidSync(buf);
        syncAsteroidPositions(asteroids, store.serverTick);
        break;
      }
      case MSG_SCENERY_SETUP: {
        const scenery = decodeScenerySetup(buf);
        store.setScenerySetup(scenery);
        break;
      }
      case MSG_PARTICLE_DEFAULTS_UPDATED: {
        // Server notifies that particle defaults changed — re-fetch via REST
        this.onParticleDefaultsUpdated?.();
        break;
      }
    }
  }

  private attemptReconnect(): void {
    if (this.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      useGameStore.getState().setConnectionStatus('disconnected');
      return;
    }

    useGameStore.getState().setConnectionStatus('reconnecting');
    this.reconnectAttempts++;

    this.reconnectTimer = setTimeout(() => {
      this.createWebSocket();
    }, RECONNECT_DELAY_MS);
  }

  private clearReconnect(): void {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }
}
